import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const BASE = "https://dailydish.in/api/admin";
const HUB_BASE = "https://dailydish.in/api/Hub";

// ---- Ink / surface ----
const INK_PRIMARY = "#0b0b0b";
const INK_SECONDARY = "#52514e";
const INK_MUTED = "#898781";
const GRID = "#e1e0d9";
const BRAND = "#6B8E23";
const BRAND_DEEP = "#3a5a0e";
const STATUS_GOOD = "#0ca30c";
const MONEY = "#1b5e20";
const WARN = "#b71c1c";

// Fixed colour per channel — follows the entity, never rank, so a sort can
// never reassign it.
const CHANNEL_COLORS = {
  flyer: "#2a78d6",
  standie: "#1baf7a",
  lift_banner: "#eda100",
  whatsapp: "#008300",
  door_tag: "#4a3aa7",
  alpine: "#e34948",
  hiranandani: "#e87ba4",
  newspace: "#3a9ea5",
  organic: "#eb6834",
  refer: "#7a5c3e",
};
const CHANNEL_LABELS = {
  flyer: "Flyer",
  standie: "Standie",
  lift_banner: "Lift Banner",
  whatsapp: "WhatsApp",
  door_tag: "Door Tag",
  alpine: "Alpine (Apartment)",
  hiranandani: "Hiranandani (Apartment)",
  newspace: "Newspace",
  organic: "Organic",
  refer: "Referral",
  socialMedia: "Social Media",
  stall: "Stall",
  sample: "Sample",
};
const channelColor = (ch) => CHANNEL_COLORS[ch] || INK_MUTED;
const channelLabel = (ch) => CHANNEL_LABELS[ch] || ch;

const SESSION_META = {
  breakfast: { label: "Breakfast", icon: "☀️", color: "#eda100" },
  lunch: { label: "Lunch", icon: "🍱", color: "#2a78d6" },
  dinner: { label: "Dinner", icon: "🌙", color: "#7d3c98" },
};
const SESSION_ORDER = ["breakfast", "lunch", "dinner"];

const rupee = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
const pct = (part, whole) =>
  whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : "—";
const shortDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "2-digit",
      })
    : "—";

// Today in IST, as the same "YYYY-MM-DD" key the API's date fields use — so the
// Today button lines up with the server's day boundaries, not the browser's.
const istToday = () => {
  const ist = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
  );
  return `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, "0")}-${String(ist.getDate()).padStart(2, "0")}`;
};
const addDaysKey = (key, delta) => {
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(y, m - 1, d + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const toKey = (d) => new Date(d).toISOString().split("T")[0];

const inputStyle = {
  padding: "9px 12px",
  borderRadius: "10px",
  border: `1.5px solid ${GRID}`,
  fontSize: "14px",
  fontFamily: "Inter",
  color: INK_PRIMARY,
};

const cardStyle = {
  background: "#fff",
  borderRadius: "14px",
  border: `1px solid ${GRID}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};

const SectionTitle = ({ children, hint }) => (
  <div style={{ margin: "26px 0 12px" }}>
    <div style={{ fontSize: "15px", fontWeight: 700, color: INK_PRIMARY }}>
      {children}
    </div>
    {hint && (
      <div style={{ fontSize: "12px", color: INK_MUTED, marginTop: "2px" }}>
        {hint}
      </div>
    )}
  </div>
);

const Tile = ({ label, value, sub, color }) => (
  <div
    style={{
      ...cardStyle,
      padding: "14px 18px",
      flex: "1 1 150px",
      minWidth: "150px",
    }}
  >
    <div style={{ fontSize: "12px", color: INK_SECONDARY }}>{label}</div>
    <div
      style={{
        fontSize: "24px",
        fontWeight: 700,
        color: color || INK_PRIMARY,
        lineHeight: 1.2,
        marginTop: "3px",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </div>
    {sub && (
      <div style={{ fontSize: "11.5px", color: INK_MUTED, marginTop: "3px" }}>
        {sub}
      </div>
    )}
  </div>
);

// Proportion bar — the only "chart" on the page. Reads as a share of the row
// maximum, so the widest bar is always the leader in that column.
const Bar = ({ value, max, color }) => (
  <div
    style={{
      background: "#f1f0ea",
      borderRadius: 99,
      height: 6,
      width: "100%",
      minWidth: 50,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        width: max > 0 ? `${Math.max(2, (value / max) * 100)}%` : "0%",
        background: color,
        height: "100%",
        borderRadius: 99,
      }}
    />
  </div>
);

const th = {
  padding: "10px 14px",
  textAlign: "left",
  fontWeight: 700,
  color: INK_MUTED,
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  whiteSpace: "nowrap",
};
const td = { padding: "10px 14px", whiteSpace: "nowrap" };

const CampaignDashboard = () => {
  const [hubs, setHubs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Date filter — scopes every number on the page to a sub-range of the campaign.
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");

  // Customer table controls
  const [search, setSearch] = useState("");
  const [repeatOnly, setRepeatOnly] = useState(false);
  const [orderedOnly, setOrderedOnly] = useState(false);
  const [sortKey, setSortKey] = useState("orderCount");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  // Create-campaign form
  const [name, setName] = useState("");
  const [hubId, setHubId] = useState("");
  const [startDate, setStartDate] = useState(toKey(new Date()));
  const [endDate, setEndDate] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchHubs = async () => {
    try {
      const res = await axios.get(`${HUB_BASE}/hubs`);
      setHubs(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${BASE}/campaigns`);
      if (res.data.success) setCampaigns(res.data.campaigns);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCampaignStats = async (campaignId) => {
    if (!campaignId) {
      setStatsData(null);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${BASE}/campaigns/${campaignId}/stats`);
      if (res.data.success) setStatsData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHubs();
    fetchCampaigns();
  }, []);

  // Default to the running campaign, else the most recent one.
  useEffect(() => {
    if (campaigns.length === 0 || selectedCampaignId) return;
    const now = new Date();
    const active = campaigns.find((c) => {
      const s = new Date(c.startDate);
      const e = c.endDate ? new Date(c.endDate) : null;
      return s <= now && (!e || now <= e);
    });
    setSelectedCampaignId((active || campaigns[0])._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns]);

  useEffect(() => {
    fetchCampaignStats(selectedCampaignId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId]);

  const campaign = statsData?.campaign;

  const campaignRange = useMemo(() => {
    if (!campaign) return null;
    return {
      start: toKey(campaign.startDate),
      end: toKey(campaign.endDate || new Date()),
    };
  }, [campaign]);

  // Reset to the full range whenever a different campaign's data lands.
  useEffect(() => {
    if (!campaignRange) return;
    setFilterStart(campaignRange.start);
    setFilterEnd(campaignRange.end);
  }, [campaignRange]);

  const inRange = (dateKey) =>
    (!filterStart || dateKey >= filterStart) &&
    (!filterEnd || dateKey <= filterEnd);

  const isTodayView = filterStart === istToday() && filterEnd === istToday();
  const isLast7View =
    filterEnd === istToday() && filterStart === addDaysKey(istToday(), -6);
  const isFullView =
    campaignRange &&
    filterStart === campaignRange.start &&
    filterEnd === campaignRange.end;

  const rangeLabel = isTodayView
    ? "Today"
    : isFullView
      ? "Full campaign"
      : `${shortDate(filterStart)} → ${shortDate(filterEnd)}`;

  // ---------- Derived slices (everything below reads from these) ----------

  const rows = useMemo(
    () => (statsData?.rows || []).filter((r) => inRange(r.date)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statsData, filterStart, filterEnd],
  );

  // Customers scoped by REGISTRATION date — "who did this campaign bring in
  // during this period".
  const customers = useMemo(
    () => (statsData?.customers || []).filter((c) => inRange(c.regDate)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statsData, filterStart, filterEnd],
  );

  // Orders scoped by DELIVERY date — "what the hub sold during this period".
  const orderRows = useMemo(
    () => (statsData?.orderRows || []).filter((r) => inRange(r.date)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statsData, filterStart, filterEnd],
  );

  // Campaign-acquired customer ids — taken from the UNFILTERED list on purpose:
  // someone who registered in week 1 and ordered in week 3 is still a campaign
  // customer when you're looking at week 3.
  const campaignCustomerIds = useMemo(
    () => new Set((statsData?.customers || []).map((c) => c.id)),
    [statsData],
  );

  const funnel = useMemo(() => {
    const scans = rows.reduce((s, r) => s + r.visits, 0);
    const registered = customers.length;
    const ordered = customers.filter((c) => c.orderCount > 0).length;
    const repeat = customers.filter((c) => c.isRepeat).length;
    const usedOffer = customers.filter((c) => c.offersUsed > 0).length;
    return { scans, registered, ordered, repeat, usedOffer };
  }, [rows, customers]);

  const hubStats = useMemo(() => {
    const totals = orderRows.reduce(
      (acc, r) => ({
        orders: acc.orders + r.orders,
        cancelled: acc.cancelled + (r.cancelled || 0),
        cancelledValue: acc.cancelledValue + (r.cancelledValue || 0),
        revenue: acc.revenue + r.revenue,
        grossValue: acc.grossValue + r.grossValue,
        discount: acc.discount + r.discount,
        offerOrders: acc.offerOrders + r.offerOrders,
      }),
      {
        orders: 0,
        cancelled: 0,
        cancelledValue: 0,
        revenue: 0,
        grossValue: 0,
        discount: 0,
        offerOrders: 0,
      },
    );

    // Distinct customers who ordered in the period, split by whether this
    // campaign brought them in.
    const newIds = new Set();
    const existingIds = new Set();
    orderRows.forEach((r) =>
      (r.customerIds || []).forEach((id) =>
        (campaignCustomerIds.has(id) ? newIds : existingIds).add(id),
      ),
    );

    return {
      ...totals,
      avgOrderValue: totals.orders > 0 ? totals.revenue / totals.orders : 0,
      // Share of everything that was placed, not of what survived.
      cancelRate:
        totals.orders + totals.cancelled > 0
          ? (totals.cancelled / (totals.orders + totals.cancelled)) * 100
          : 0,
      newCustomersOrdered: newIds.size,
      existingCustomersOrdered: existingIds.size,
    };
  }, [orderRows, campaignCustomerIds]);

  const sessionStats = useMemo(() => {
    const map = {};
    SESSION_ORDER.forEach((s) => {
      map[s] = {
        session: s,
        orders: 0,
        cancelled: 0,
        revenue: 0,
        discount: 0,
        customers: new Set(),
      };
    });
    orderRows.forEach((r) => {
      if (!map[r.session]) {
        map[r.session] = {
          session: r.session,
          orders: 0,
          cancelled: 0,
          revenue: 0,
          discount: 0,
          customers: new Set(),
        };
      }
      map[r.session].orders += r.orders;
      map[r.session].cancelled += r.cancelled || 0;
      map[r.session].revenue += r.revenue;
      map[r.session].discount += r.discount;
      (r.customerIds || []).forEach((id) => map[r.session].customers.add(id));
    });
    return Object.values(map).map((s) => ({
      ...s,
      customers: s.customers.size,
      avgOrderValue: s.orders > 0 ? s.revenue / s.orders : 0,
    }));
  }, [orderRows]);

  const topSession = useMemo(() => {
    const ranked = [...sessionStats].sort((a, b) => b.orders - a.orders);
    return ranked[0]?.orders > 0 ? ranked[0] : null;
  }, [sessionStats]);

  const channelStats = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (!map[r.channel])
        map[r.channel] = {
          channel: r.channel,
          visits: 0,
          registered: 0,
          ordered: 0,
        };
      map[r.channel].visits += r.visits;
      map[r.channel].registered += r.registered;
      map[r.channel].ordered += r.ordered;
    });
    return Object.values(map)
      .map((c) => ({
        ...c,
        regRate: c.visits > 0 ? (c.registered / c.visits) * 100 : null,
        orderRate: c.registered > 0 ? (c.ordered / c.registered) * 100 : null,
      }))
      .sort((a, b) => b.visits - a.visits);
  }, [rows]);

  const maxChannelVisits = Math.max(1, ...channelStats.map((c) => c.visits));
  const maxSessionOrders = Math.max(1, ...sessionStats.map((s) => s.orders));

  // How each sortable column pulls its comparison value. Dates become numbers so
  // "never ordered" sorts to one end instead of landing randomly.
  const SORT_VALUE = {
    name: (c) => (c.name || "").toLowerCase(),
    mobile: (c) => c.mobile || "",
    channel: (c) => channelLabel(c.channel).toLowerCase(),
    registeredAt: (c) => new Date(c.registeredAt).getTime(),
    orderCount: (c) => c.orderCount,
    cancelledOrders: (c) => c.cancelledOrders || 0,
    lastOrderAt: (c) => (c.lastOrderAt ? new Date(c.lastOrderAt).getTime() : 0),
    totalSpend: (c) => c.totalSpend,
    offersUsed: (c) => c.offersUsed,
    isRepeat: (c) => (c.isRepeat ? 2 : c.orderCount > 0 ? 1 : 0),
  };

  const visibleCustomers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pick = SORT_VALUE[sortKey] || SORT_VALUE.orderCount;
    const dir = sortDir === "asc" ? 1 : -1;
    return customers
      .filter((c) => {
        if (repeatOnly && !c.isRepeat) return false;
        if (orderedOnly && c.orderCount === 0) return false;
        if (!q) return true;
        return (
          c.name?.toLowerCase().includes(q) ||
          c.mobile?.includes(q) ||
          channelLabel(c.channel).toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const av = pick(a);
        const bv = pick(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        // Stable tie-break so equal rows never shuffle between renders.
        return b.totalSpend - a.totalSpend;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, search, repeatOnly, orderedOnly, sortKey, sortDir]);

  const PAGE_SIZE = 50;
  const totalPages = Math.max(
    1,
    Math.ceil(visibleCustomers.length / PAGE_SIZE),
  );
  const safePage = Math.min(page, totalPages);
  const pagedCustomers = useMemo(
    () =>
      visibleCustomers.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [visibleCustomers, safePage],
  );

  // Any change to what is being listed sends you back to page 1 — otherwise you
  // can land on an empty page 7 after narrowing a filter.
  useEffect(() => {
    setPage(1);
  }, [
    search,
    repeatOnly,
    orderedOnly,
    sortKey,
    sortDir,
    filterStart,
    filterEnd,
    selectedCampaignId,
  ]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Text reads naturally A→Z; numbers are almost always wanted biggest-first.
      setSortDir(["name", "mobile", "channel"].includes(key) ? "asc" : "desc");
    }
  };

  // ---------- Excel export ----------

  const exportToExcel = () => {
    if (!campaign) return;

    const summary = [
      ["Campaign", campaign.name],
      ["Hub", campaign.hubName],
      ["Period shown", `${filterStart} to ${filterEnd}`],
      ["Generated", new Date().toLocaleString("en-IN")],
      [],
      ["ACQUISITION"],
      ["Total scans", funnel.scans],
      ["Registered", funnel.registered],
      ["Registered / scans", pct(funnel.registered, funnel.scans)],
      ["Registered who ordered", funnel.ordered],
      ["Ordered / registered", pct(funnel.ordered, funnel.registered)],
      ["Repeat customers (2+ orders)", funnel.repeat],
      ["Used the offer at least once", funnel.usedOffer],
      [],
      ["HUB ORDERS IN PERIOD"],
      ["Total orders (confirmed)", hubStats.orders],
      ["Total order amount (paid)", hubStats.revenue],
      ["Gross order value", hubStats.grossValue],
      ["Cancelled orders", hubStats.cancelled],
      ["Cancelled order value", hubStats.cancelledValue],
      ["Cancellation rate", `${hubStats.cancelRate.toFixed(1)}%`],
      ["Offer discount given", hubStats.discount],
      ["Average order value", Math.round(hubStats.avgOrderValue)],
      ["Orders using an offer", hubStats.offerOrders],
      ["New (campaign) customers who ordered", hubStats.newCustomersOrdered],
      ["Existing customers who ordered", hubStats.existingCustomersOrdered],
      [
        "Best session by orders",
        topSession
          ? SESSION_META[topSession.session]?.label || topSession.session
          : "—",
      ],
    ];

    const sessionSheet = sessionStats.map((s) => ({
      Session: SESSION_META[s.session]?.label || s.session,
      Orders: s.orders,
      "Order Amount": s.revenue,
      "Avg Order Value": Math.round(s.avgOrderValue),
      Cancelled: s.cancelled,
      "Offer Discount": s.discount,
      "Distinct Customers": s.customers,
      "Share of Orders": pct(s.orders, hubStats.orders),
    }));

    const channelSheet = channelStats.map((c) => ({
      Channel: channelLabel(c.channel),
      Scans: c.visits,
      Registered: c.registered,
      Ordered: c.ordered,
      "Reg Rate": c.regRate === null ? "—" : `${c.regRate.toFixed(1)}%`,
      "Order Rate": c.orderRate === null ? "—" : `${c.orderRate.toFixed(1)}%`,
    }));

    const customerSheet = visibleCustomers.map((c, i) => ({
      "#": i + 1,
      Name: c.name || "—",
      Mobile: c.mobile || "—",
      Channel: channelLabel(c.channel),
      Registered: shortDate(c.registeredAt),
      Orders: c.orderCount,
      Cancelled: c.cancelledOrders || 0,
      "First Order": shortDate(c.firstOrderAt),
      "Last Order": shortDate(c.lastOrderAt),
      "Total Spend": c.totalSpend,
      "Offers Used": c.offersUsed,
      "Offers Allotted": c.offersTotal,
      "Offer Savings": c.offerSavings,
      "Repeat Customer": c.isRepeat ? "Yes" : "No",
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(summary),
      "Summary",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(sessionSheet),
      "Sessions",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(channelSheet),
      "Channels",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(customerSheet),
      "Customers",
    );

    const safeName = campaign.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    XLSX.writeFile(wb, `${safeName}_${filterStart}_to_${filterEnd}.xlsx`);
  };

  const handleCreateCampaign = async () => {
    if (!name || !hubId || !startDate) {
      alert("Name, hub and start date are required.");
      return;
    }
    try {
      setCreating(true);
      const hub = hubs.find((h) => h._id === hubId);
      const res = await axios.post(`${BASE}/campaigns`, {
        name,
        hubId,
        hubName: hub?.hubName || "",
        startDate,
        endDate: endDate || null,
      });
      if (res.data.success) {
        setName("");
        setHubId("");
        setEndDate("");
        setShowCreateForm(false);
        await fetchCampaigns();
        setSelectedCampaignId(res.data.campaign._id);
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to create campaign");
    } finally {
      setCreating(false);
    }
  };

  const isActive =
    campaign &&
    new Date(campaign.startDate) <= new Date() &&
    (!campaign.endDate || new Date() <= new Date(campaign.endDate));

  const rangeBtn = (label, active, onClick) => (
    <button
      key={label}
      onClick={onClick}
      style={{
        padding: "8px 14px",
        borderRadius: "999px",
        border: `1.5px solid ${active ? BRAND : GRID}`,
        background: active ? BRAND : "#fff",
        color: active ? "#fff" : INK_SECONDARY,
        fontWeight: 600,
        fontSize: "13px",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Inter, sans-serif",
        background: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: `linear-gradient(135deg, ${BRAND} 0%, #4a6e12 100%)`,
          borderRadius: "14px",
          padding: "20px 24px",
          color: "#fff",
          marginBottom: "18px",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 700 }}>
          🎯 Campaign Dashboard
        </h2>
        <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: "13.5px" }}>
          Who we reached, who signed up, who ordered — and what the hub earned.
        </p>
      </div>

      {/* Campaign switcher */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: "14px",
        }}
      >
        <select
          value={selectedCampaignId}
          onChange={(e) => setSelectedCampaignId(e.target.value)}
          style={{ ...inputStyle, minWidth: "300px" }}
        >
          <option value="">Select a campaign</option>
          {campaigns.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name} · {c.hubName} · from{" "}
              {new Date(c.startDate).toLocaleDateString("en-IN")}
              {c.endDate
                ? ` to ${new Date(c.endDate).toLocaleDateString("en-IN")}`
                : " (ongoing)"}
            </option>
          ))}
        </select>
        <button
          onClick={() => setShowCreateForm((v) => !v)}
          style={{
            padding: "9px 16px",
            borderRadius: "10px",
            border: `1.5px solid ${BRAND}`,
            background: showCreateForm ? BRAND : "#fff",
            color: showCreateForm ? "#fff" : BRAND,
            fontWeight: 700,
            fontSize: "13px",
            cursor: "pointer",
          }}
        >
          {showCreateForm ? "✕ Cancel" : "+ New Campaign"}
        </button>
      </div>

      {showCreateForm && (
        <div style={{ ...cardStyle, padding: "18px", marginBottom: "18px" }}>
          <h3
            style={{
              margin: "0 0 12px",
              fontSize: "14px",
              color: BRAND_DEEP,
              fontWeight: 700,
            }}
          >
            Create Campaign
          </h3>
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            {[
              {
                label: "Campaign Name",
                el: (
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. July Flyer Drive"
                    style={{ ...inputStyle, minWidth: "220px" }}
                  />
                ),
              },
              {
                label: "Hub",
                el: (
                  <select
                    value={hubId}
                    onChange={(e) => setHubId(e.target.value)}
                    style={{ ...inputStyle, minWidth: "220px" }}
                  >
                    <option value="">Select a hub</option>
                    {hubs.map((h) => (
                      <option key={h._id} value={h._id}>
                        {h.hubName}
                      </option>
                    ))}
                  </select>
                ),
              },
              {
                label: "Start Date",
                el: (
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={inputStyle}
                  />
                ),
              },
              {
                label: "End Date (optional)",
                el: (
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={inputStyle}
                  />
                ),
              },
            ].map(({ label, el }) => (
              <div key={label}>
                <div
                  style={{
                    fontSize: "12px",
                    color: INK_SECONDARY,
                    fontWeight: 600,
                    marginBottom: "6px",
                  }}
                >
                  {label}
                </div>
                {el}
              </div>
            ))}
            <button
              onClick={handleCreateCampaign}
              disabled={creating}
              style={{
                padding: "10px 20px",
                borderRadius: "10px",
                border: "none",
                background: BRAND,
                color: "#fff",
                fontWeight: 700,
                cursor: creating ? "not-allowed" : "pointer",
              }}
            >
              {creating ? "Creating..." : "Create Campaign"}
            </button>
          </div>
        </div>
      )}

      {!selectedCampaignId ? (
        <div
          style={{
            ...cardStyle,
            textAlign: "center",
            padding: "60px",
            color: INK_MUTED,
          }}
        >
          {campaigns.length === 0
            ? "No campaigns yet — create one to start tracking a hub's performance."
            : "Select a campaign above to see its analysis."}
        </div>
      ) : loading ? (
        <div
          style={{
            ...cardStyle,
            textAlign: "center",
            padding: "60px",
            color: INK_MUTED,
          }}
        >
          Loading campaign data…
        </div>
      ) : (
        <>
          {/* Campaign meta */}
          {campaign && (
            <div
              style={{
                ...cardStyle,
                padding: "12px 18px",
                marginBottom: "14px",
                display: "flex",
                gap: "18px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "15px",
                  color: INK_PRIMARY,
                }}
              >
                {campaign.name}
              </div>
              <div style={{ fontSize: "13px", color: INK_SECONDARY }}>
                🏬 {campaign.hubName}
              </div>
              <div style={{ fontSize: "13px", color: INK_SECONDARY }}>
                📅 {shortDate(campaign.startDate)} →{" "}
                {campaign.endDate ? shortDate(campaign.endDate) : "ongoing"}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: isActive ? STATUS_GOOD : INK_MUTED,
                }}
              >
                ● {isActive ? "Active" : "Ended"}
              </div>
            </div>
          )}

          {/* Range controls + export */}
          <div
            style={{
              ...cardStyle,
              padding: "14px 18px",
              marginBottom: "6px",
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {rangeBtn("Today", isTodayView, () => {
              setFilterStart(istToday());
              setFilterEnd(istToday());
            })}
            {rangeBtn("Last 7 days", isLast7View, () => {
              setFilterStart(addDaysKey(istToday(), -6));
              setFilterEnd(istToday());
            })}
            {rangeBtn("Full campaign", isFullView, () => {
              if (!campaignRange) return;
              setFilterStart(campaignRange.start);
              setFilterEnd(campaignRange.end);
            })}

            <div
              style={{
                width: "1px",
                height: "26px",
                background: GRID,
                margin: "0 4px",
              }}
            />

            <input
              type="date"
              value={filterStart}
              max={filterEnd || undefined}
              onChange={(e) => setFilterStart(e.target.value)}
              style={inputStyle}
            />
            <span style={{ color: INK_MUTED, fontSize: "13px" }}>to</span>
            <input
              type="date"
              value={filterEnd}
              min={filterStart || undefined}
              onChange={(e) => setFilterEnd(e.target.value)}
              style={inputStyle}
            />

            <button
              onClick={exportToExcel}
              title="Downloads every row matching the current filters — not just the page on screen"
              style={{
                marginLeft: "auto",
                padding: "9px 16px",
                borderRadius: "10px",
                border: `1.5px solid ${MONEY}`,
                background: "#fff",
                color: MONEY,
                fontWeight: 700,
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              ⬇ Export to Excel
            </button>
          </div>

          <div
            style={{
              fontSize: "12px",
              color: INK_MUTED,
              marginBottom: "16px",
              paddingLeft: "4px",
            }}
          >
            Showing:{" "}
            <strong style={{ color: INK_SECONDARY }}>{rangeLabel}</strong>
          </div>

          {/* ── 1. Acquisition funnel ── */}
          <SectionTitle hint="Scans come from all channels; registrations and orders are limited to this hub.">
            1 · Did the campaign bring people in?
          </SectionTitle>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <Tile
              label="Total scans"
              value={funnel.scans.toLocaleString("en-IN")}
            />
            <Tile
              label="Registered"
              value={funnel.registered.toLocaleString("en-IN")}
              sub={`${pct(funnel.registered, funnel.scans)} of scans`}
              color={BRAND_DEEP}
            />
            <Tile
              label="Placed an order"
              value={funnel.ordered.toLocaleString("en-IN")}
              sub={`${pct(funnel.ordered, funnel.registered)} of registered`}
              color={BRAND_DEEP}
            />
            <Tile
              label="Repeat customers"
              value={funnel.repeat.toLocaleString("en-IN")}
              sub={`2+ orders · ${pct(funnel.repeat, funnel.ordered)} of those who ordered`}
              color="#e65100"
            />
            <Tile
              label="Used the offer"
              value={funnel.usedOffer.toLocaleString("en-IN")}
              sub={`${pct(funnel.usedOffer, funnel.registered)} of registered`}
              color="#6a1b9a"
            />
          </div>

          {/* ── 2. Hub orders / money ── */}
          <SectionTitle hint="Every confirmed order this hub took in the selected period — campaign customers and everyone else. Cancelled orders are excluded from all money figures and reported separately.">
            2 · What did the hub earn?
          </SectionTitle>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <Tile
              label="Total orders"
              value={hubStats.orders.toLocaleString("en-IN")}
            />
            <Tile
              label="Total order amount"
              value={rupee(hubStats.revenue)}
              sub={`Gross ${rupee(hubStats.grossValue)} before discounts`}
              color={MONEY}
            />
            <Tile
              label="Avg order value"
              value={rupee(hubStats.avgOrderValue)}
              color={MONEY}
            />
            <Tile
              label="Offer discount given"
              value={rupee(hubStats.discount)}
              sub={`${hubStats.offerOrders} order${hubStats.offerOrders === 1 ? "" : "s"} used an offer`}
              color="#6a1b9a"
            />
            <Tile
              label="Cancelled orders"
              value={hubStats.cancelled.toLocaleString("en-IN")}
              sub={
                hubStats.cancelled > 0
                  ? `${hubStats.cancelRate.toFixed(1)}% of all placed · ${rupee(hubStats.cancelledValue)} lost`
                  : "None cancelled"
              }
              color={hubStats.cancelled > 0 ? WARN : INK_MUTED}
            />
            <Tile
              label="New customers ordered"
              value={hubStats.newCustomersOrdered.toLocaleString("en-IN")}
              sub="Acquired by this campaign"
              color={BRAND_DEEP}
            />
            <Tile
              label="Existing customers ordered"
              value={hubStats.existingCustomersOrdered.toLocaleString("en-IN")}
              sub="Already with us before the campaign"
              color="#1565c0"
            />
          </div>

          {/* ── 3. Sessions ── */}
          <SectionTitle
            hint={
              topSession
                ? `${SESSION_META[topSession.session]?.label || topSession.session} is the busiest session in this period.`
                : "No orders in this period."
            }
          >
            3 · Which session sells best?
          </SectionTitle>
          <div style={{ ...cardStyle, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: `2px solid ${GRID}` }}>
                    {[
                      "Session",
                      "Orders",
                      "Share",
                      "Order Amount",
                      "Avg Order Value",
                      "Cancelled",
                      "Offer Discount",
                      "Customers",
                    ].map((h) => (
                      <th key={h} style={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
                  {sessionStats.map((s) => {
                    const meta = SESSION_META[s.session] || {
                      label: s.session,
                      icon: "🍽️",
                      color: INK_MUTED,
                    };
                    const isTop = topSession?.session === s.session;
                    return (
                      <tr
                        key={s.session}
                        style={{
                          borderBottom: `1px solid ${GRID}`,
                          background: isTop ? "#f6faf0" : "#fff",
                        }}
                      >
                        <td style={{ ...td, fontWeight: 600 }}>
                          {meta.icon} {meta.label}
                          {isTop && s.orders > 0 && (
                            <span
                              style={{
                                marginLeft: "8px",
                                fontSize: "10.5px",
                                fontWeight: 700,
                                color: BRAND_DEEP,
                                background: "#e8f0d8",
                                borderRadius: "999px",
                                padding: "2px 8px",
                              }}
                            >
                              BUSIEST
                            </span>
                          )}
                        </td>
                        <td style={{ ...td, fontWeight: 700 }}>{s.orders}</td>
                        <td style={{ ...td, width: "160px" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Bar
                              value={s.orders}
                              max={maxSessionOrders}
                              color={meta.color}
                            />
                            <span
                              style={{ fontSize: "12px", color: INK_MUTED }}
                            >
                              {pct(s.orders, hubStats.orders)}
                            </span>
                          </div>
                        </td>
                        <td style={td}>{rupee(s.revenue)}</td>
                        <td style={td}>{rupee(s.avgOrderValue)}</td>
                        <td
                          style={{
                            ...td,
                            color: s.cancelled > 0 ? WARN : INK_MUTED,
                            fontWeight: s.cancelled > 0 ? 600 : 400,
                          }}
                        >
                          {s.cancelled || "—"}
                        </td>
                        <td
                          style={{
                            ...td,
                            color: s.discount > 0 ? "#6a1b9a" : INK_MUTED,
                          }}
                        >
                          {s.discount > 0 ? rupee(s.discount) : "—"}
                        </td>
                        <td style={td}>{s.customers}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr
                    style={{ borderTop: `2px solid ${GRID}`, fontWeight: 700 }}
                  >
                    <td style={td}>Total</td>
                    <td style={td}>{hubStats.orders}</td>
                    <td style={td}>—</td>
                    <td style={td}>{rupee(hubStats.revenue)}</td>
                    <td style={td}>{rupee(hubStats.avgOrderValue)}</td>
                    <td style={td}>{hubStats.cancelled || "—"}</td>
                    <td style={td}>{rupee(hubStats.discount)}</td>
                    <td style={td}>—</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* ── 4. Channels ── */}
          <SectionTitle hint="Scans are recorded per channel across all hubs; registrations and orders are this hub only.">
            4 · Which marketing channel worked?
          </SectionTitle>
          <div style={{ ...cardStyle, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: `2px solid ${GRID}` }}>
                    {[
                      "Channel",
                      "Scans",
                      "",
                      "Registered",
                      "Ordered",
                      "Sign-up Rate",
                      "Order Rate",
                    ].map((h, i) => (
                      <th key={i} style={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
                  {channelStats.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        style={{
                          ...td,
                          textAlign: "center",
                          color: INK_MUTED,
                          padding: "32px",
                        }}
                      >
                        No scans recorded in this period.
                      </td>
                    </tr>
                  ) : (
                    channelStats.map((c) => (
                      <tr
                        key={c.channel}
                        style={{ borderBottom: `1px solid ${GRID}` }}
                      >
                        <td style={{ ...td, fontWeight: 600 }}>
                          <span
                            style={{
                              display: "inline-block",
                              width: "9px",
                              height: "9px",
                              borderRadius: "50%",
                              background: channelColor(c.channel),
                              marginRight: "8px",
                            }}
                          />
                          {channelLabel(c.channel)}
                        </td>
                        <td style={{ ...td, fontWeight: 700 }}>{c.visits}</td>
                        <td style={{ ...td, width: "140px" }}>
                          <Bar
                            value={c.visits}
                            max={maxChannelVisits}
                            color={channelColor(c.channel)}
                          />
                        </td>
                        <td style={td}>{c.registered}</td>
                        <td style={td}>{c.ordered}</td>
                        <td style={td}>
                          {c.regRate === null
                            ? "—"
                            : `${c.regRate.toFixed(1)}%`}
                        </td>
                        <td style={td}>
                          {c.orderRate === null
                            ? "—"
                            : `${c.orderRate.toFixed(1)}%`}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {channelStats.length > 0 && (
                  <tfoot>
                    <tr
                      style={{
                        borderTop: `2px solid ${GRID}`,
                        fontWeight: 700,
                      }}
                    >
                      <td style={td}>Total</td>
                      <td style={td}>{funnel.scans}</td>
                      <td style={td} />
                      <td style={td}>{funnel.registered}</td>
                      <td style={td}>{funnel.ordered}</td>
                      <td style={td}>{pct(funnel.registered, funnel.scans)}</td>
                      <td style={td}>
                        {pct(funnel.ordered, funnel.registered)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* ── 5. Customers ── */}
          <SectionTitle hint="Everyone who registered at this hub during the selected period.">
            5 · Who signed up?
          </SectionTitle>
          <div style={{ ...cardStyle, overflow: "hidden" }}>
            <div
              style={{
                padding: "14px 18px",
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                alignItems: "center",
                borderBottom: `1px solid ${GRID}`,
              }}
            >
              <input
                type="text"
                placeholder="Search name, mobile or channel…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ ...inputStyle, minWidth: "240px" }}
              />
              {[
                ["Repeat customers only", repeatOnly, setRepeatOnly],
                ["Ordered at least once", orderedOnly, setOrderedOnly],
              ].map(([label, val, set]) => (
                <label
                  key={label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "13px",
                    color: INK_SECONDARY,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={val}
                    onChange={(e) => set(e.target.checked)}
                  />
                  {label}
                </label>
              ))}
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "12px",
                  color: INK_MUTED,
                }}
              >
                {visibleCustomers.length === customers.length
                  ? `${customers.length} customers`
                  : `${visibleCustomers.length} of ${customers.length} customers`}
              </span>
            </div>

            <div style={{ overflowX: "auto", maxHeight: "620px" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                }}
              >
                <thead
                  style={{ position: "sticky", top: 0, background: "#fff" }}
                >
                  <tr style={{ borderBottom: `2px solid ${GRID}` }}>
                    <th style={th}>#</th>
                    {[
                      ["Name", "name"],
                      ["Mobile", "mobile"],
                      ["Channel", "channel"],
                      ["Registered", "registeredAt"],
                      ["Orders", "orderCount"],
                      ["Cancelled", "cancelledOrders"],
                      ["Last Order", "lastOrderAt"],
                      ["Total Spend", "totalSpend"],
                      ["Offers Used", "offersUsed"],
                      ["Type", "isRepeat"],
                    ].map(([label, key]) => (
                      <th
                        key={key}
                        onClick={() => toggleSort(key)}
                        title={`Sort by ${label}`}
                        style={{
                          ...th,
                          cursor: "pointer",
                          userSelect: "none",
                          color: sortKey === key ? BRAND_DEEP : INK_MUTED,
                        }}
                      >
                        {label}
                        <span style={{ marginLeft: "4px" }}>
                          {sortKey === key
                            ? sortDir === "asc"
                              ? "▲"
                              : "▼"
                            : "⇅"}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
                  {visibleCustomers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={11}
                        style={{
                          ...td,
                          textAlign: "center",
                          color: INK_MUTED,
                          padding: "32px",
                        }}
                      >
                        No customers match this filter.
                      </td>
                    </tr>
                  ) : (
                    pagedCustomers.map((c, i) => (
                      <tr
                        key={c.id}
                        style={{ borderBottom: `1px solid ${GRID}` }}
                      >
                        <td style={{ ...td, color: INK_MUTED }}>
                          {(safePage - 1) * PAGE_SIZE + i + 1}
                        </td>
                        <td style={{ ...td, fontWeight: 600 }}>
                          {c.name || "—"}
                        </td>
                        <td style={td}>{c.mobile || "—"}</td>
                        <td style={td}>
                          <span
                            style={{
                              display: "inline-block",
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              background: channelColor(c.channel),
                              marginRight: "7px",
                            }}
                          />
                          {channelLabel(c.channel)}
                        </td>
                        <td style={{ ...td, color: INK_SECONDARY }}>
                          {shortDate(c.registeredAt)}
                        </td>
                        <td style={{ ...td, fontWeight: 700 }}>
                          {c.orderCount}
                        </td>
                        <td
                          style={{
                            ...td,
                            color: c.cancelledOrders > 0 ? WARN : INK_MUTED,
                          }}
                        >
                          {c.cancelledOrders || "—"}
                        </td>
                        <td style={{ ...td, color: INK_SECONDARY }}>
                          {c.lastOrderAt ? shortDate(c.lastOrderAt) : "Never"}
                        </td>
                        <td style={td}>
                          {c.totalSpend > 0 ? rupee(c.totalSpend) : "—"}
                        </td>
                        <td style={td}>
                          {c.offersTotal > 0
                            ? `${c.offersUsed} / ${c.offersTotal}`
                            : c.offersUsed || "—"}
                        </td>
                        <td style={td}>
                          {c.isRepeat ? (
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: 700,
                                color: "#e65100",
                                background: "#fff3e0",
                                borderRadius: "999px",
                                padding: "3px 9px",
                              }}
                            >
                              Repeat
                            </span>
                          ) : c.orderCount > 0 ? (
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: 700,
                                color: BRAND_DEEP,
                                background: "#eef4e2",
                                borderRadius: "999px",
                                padding: "3px 9px",
                              }}
                            >
                              Ordered once
                            </span>
                          ) : (
                            <span
                              style={{ fontSize: "11px", color: INK_MUTED }}
                            >
                              Never ordered
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div
                style={{
                  padding: "12px 18px",
                  borderTop: `1px solid ${GRID}`,
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontSize: "12.5px", color: INK_SECONDARY }}>
                  Showing{" "}
                  <strong>
                    {(safePage - 1) * PAGE_SIZE + 1}–
                    {Math.min(safePage * PAGE_SIZE, visibleCustomers.length)}
                  </strong>{" "}
                  of {visibleCustomers.length}
                </span>
                <div
                  style={{ marginLeft: "auto", display: "flex", gap: "6px" }}
                >
                  {[
                    ["‹ Prev", safePage === 1, () => setPage(safePage - 1)],
                    [
                      "Next ›",
                      safePage === totalPages,
                      () => setPage(safePage + 1),
                    ],
                  ].map(([label, disabled, onClick]) => (
                    <button
                      key={label}
                      onClick={onClick}
                      disabled={disabled}
                      style={{
                        padding: "7px 14px",
                        borderRadius: "8px",
                        border: `1.5px solid ${GRID}`,
                        background: "#fff",
                        color: disabled ? INK_MUTED : INK_PRIMARY,
                        fontWeight: 600,
                        fontSize: "13px",
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: disabled ? 0.5 : 1,
                      }}
                    >
                      {label}
                    </button>
                  ))}
                  <span
                    style={{
                      fontSize: "12.5px",
                      color: INK_SECONDARY,
                      alignSelf: "center",
                      padding: "0 4px",
                    }}
                  >
                    Page {safePage} / {totalPages}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div style={{ height: "40px" }} />
        </>
      )}
    </div>
  );
};

export default CampaignDashboard;
